/**
 * useAdaSupply — V2.K.7
 *
 * Fetches ADA supply data from CoinGecko.
 *
 * Cardano (ADA) tokenomics :
 *   - **Hard cap : 45 B ADA** (immutable).
 *   - At genesis : ~31.1 B distributed (Public Sale 57.62 %, IOHK
 *     5.5 %, EMURGO 4.6 %, Foundation 1.42 %) + 13.9 B reserved as
 *     monetary expansion reserve.
 *   - **Reserve depletion (ρ = 0.3 %)** : each 5-day epoch, 0.3 % of
 *     remaining reserves are released. Result : smooth exponential
 *     decline of the reserve with a half-life of ~4-5 years.
 *   - **Treasury share (τ = 20 %)** : 20 % of per-epoch monetary
 *     expansion goes to the on-chain treasury. The remaining 80 %
 *     funds SPO and delegator rewards.
 *   - **Voltaire + Chang hard fork (2024-2025)** put treasury and
 *     protocol parameters under on-chain DRep / SPO / CC governance.
 *     Net Change Limit 2025 capped treasury withdrawals at 350 M ADA.
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface AdaSupplyData {
  circulating: number;
  total: number;
  max: number;
  /** Reserve still to be released over time (= max − total). */
  reserveRemaining: number;
  /** % of the 45 B cap already minted (= total / max). */
  mintedPct: number;
  /** Published reference on-chain treasury balance. */
  treasuryReference: number;
  /** Annual ADA flowing into the treasury (= 20 % of expansion). */
  annualTreasuryFlow: number;
  /** Annual ADA released from reserves (approximation = reserve × 1 − (1−ρ)^73). */
  annualReserveRelease: number;
  lastUpdated: string | null;
}

// Hard cap from Cardano whitepaper.
const ADA_MAX_SUPPLY = 45_000_000_000;
// Cardano docs: per-epoch ρ = 0.3 % of remaining reserves. 73 epochs
// per year (epoch = 5 days). Annual release approximation :
// 1 − (1 − ρ)^73 ≈ 19.7 % of remaining reserves released per year.
const ADA_ANNUAL_RELEASE_FRACTION = 1 - Math.pow(1 - 0.003, 73);
// Reference values (Cardano docs + Messari State of Cardano Q4 2025).
const ADA_TREASURY_REFERENCE = 1_690_000_000;
const ADA_ANNUAL_TREASURY_FLOW_REFERENCE = 315_320_000;

async function fetchAdaSupply(): Promise<AdaSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/cardano?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  // CoinGecko's "total_supply" for ADA = currently minted (excludes
  // reserves). We use the max_supply field directly for the cap.
  const total = Number(md.total_supply) || circulating;
  const max = Number(md.max_supply) || ADA_MAX_SUPPLY;
  const reserveRemaining = Math.max(0, max - total);

  return {
    circulating,
    total,
    max,
    reserveRemaining,
    mintedPct: max > 0 ? (total / max) * 100 : 0,
    treasuryReference: ADA_TREASURY_REFERENCE,
    annualTreasuryFlow: ADA_ANNUAL_TREASURY_FLOW_REFERENCE,
    annualReserveRelease: reserveRemaining * ADA_ANNUAL_RELEASE_FRACTION,
    lastUpdated: json.last_updated || null,
  };
}

export function useAdaSupply() {
  return useQuery({
    queryKey: ['ada', 'supply'],
    queryFn: fetchAdaSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
